// src/redux/slices/monitorSlice.js
import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = 'https://gritwears.store/api';

const initialState = {
  activeMonitors: [],
  loading: false,
  error: null,
  lastUpdated: null,
};

const monitorSlice = createSlice({
  name: 'monitors',
  initialState,
  reducers: {
    setMonitors: (state, action) => {
      state.activeMonitors = Array.isArray(action.payload) ? action.payload : [];
      state.loading = false;
      state.lastUpdated = Date.now();
      state.error = null;
    },
    clearMonitors: (state) => {
      state.activeMonitors = [];
      state.loading = false;
      state.error = null;
      state.lastUpdated = Date.now();
    },
    addMonitor: (state, action) => {
      if (Array.isArray(state.activeMonitors)) {
        state.activeMonitors.push(action.payload);
      } else {
        state.activeMonitors = [action.payload];
      }
      state.lastUpdated = Date.now();
    },
    removeMonitor: (state, action) => {
      if (Array.isArray(state.activeMonitors)) {
        state.activeMonitors = state.activeMonitors.filter(
          m => m.id !== action.payload && `${m.symbol}_${m.interval}` !== action.payload
        );
        state.lastUpdated = Date.now();
      }
    },
    removeMonitorsBySymbol: (state, action) => {
      const symbol = action.payload;
      if (Array.isArray(state.activeMonitors)) {
        state.activeMonitors = state.activeMonitors.filter(
          m => m.symbol !== symbol
        );
        state.lastUpdated = Date.now();
      }
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const { 
  setMonitors, 
  addMonitor, 
  removeMonitor, 
  removeMonitorsBySymbol,
  clearMonitors,
  setLoading, 
  setError 
} = monitorSlice.actions;

// Async actions
export const loadUserMonitors = (userId) => async (dispatch) => {
  if (!userId) {
    console.error('No userId provided to loadUserMonitors');
    dispatch(setMonitors([]));
    return;
  }
  
  dispatch(setLoading(true));
  try {
    const response = await axios.get(`${API_URL}/active/${userId}`);
    const monitors = Array.isArray(response.data) ? response.data : [];
    dispatch(setMonitors(monitors));
    return monitors;
  } catch (error) {
    console.error('Error loading monitors:', error);
    dispatch(setError(error.message));
    dispatch(setMonitors([]));
    return [];
  }
};

export const startMonitoring = (userId, symbol, intervals, direction) => async (dispatch) => {
  if (!userId) {
    toast.error('User not authenticated');
    return false;
  }
  
  dispatch(setLoading(true));
  try {
    const response = await axios.post(`${API_URL}/start`, {
      userId,
      symbol,
      intervals,
      direction
    });
    if (response.data.success) {
      await dispatch(loadUserMonitors(userId));
      toast.success(`Started monitoring ${symbol}`);
      return true;
    }
    toast.error(response.data.message || 'Failed to start monitoring');
    return false;
  } catch (error) {
    console.error('Error starting monitoring:', error);
    toast.error('Error starting monitoring');
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export const stopSymbolMonitoring = (userId, symbol) => async (dispatch) => {
  if (!userId) {
    toast.error('User not authenticated');
    return false;
  }
  
  dispatch(setLoading(true));
  try {
    const response = await axios.post(`${API_URL}/stop-symbol`, { userId, symbol });
    if (response.data.success) {
      // Immediately remove from Redux state
      dispatch(removeMonitorsBySymbol(symbol));
      // Then reload to ensure sync with Firebase
      await dispatch(loadUserMonitors(userId));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error stopping monitor:', error);
    toast.error('Error stopping monitor');
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export const stopAllMonitoring = (userId) => async (dispatch) => {
  if (!userId) {
    toast.error('User not authenticated');
    return false;
  }
  
  dispatch(setLoading(true));
  try {
    const response = await axios.post(`${API_URL}/stop-all`, { userId });
    if (response.data.success) {
      // Immediately clear Redux state
      dispatch(clearMonitors());
      // Then reload to ensure sync with Firebase
      await dispatch(loadUserMonitors(userId));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error stopping monitors:', error);
    toast.error('Error stopping monitors');
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export default monitorSlice.reducer;